import React from 'react';
import { MapPinIcon } from 'lucide-react';
import { useDarkClasses } from '../../../hooks/useDarkClasses';
import { VENUE_TYPES, VIRTUAL_PLATFORMS, COUNTRIES } from '../../../data/eventSchema';
import type { EM } from './useEventManager';
export const LocationSection: React.FC<{
  em: EM;
}> = ({
  em
}) => {
  const dk = useDarkClasses();
  const {
    formData,
    updateField,
    updateNestedField
  } = em;
  // Helper to determine what to show
  const showInPerson = !formData.isVirtual || formData.isVirtual === undefined; // Default to in-person
  const showVirtual = formData.isVirtual;
  return <div className={`p-6 rounded-xl shadow-sm ${dk.card}`} data-id="element-18582" data-mp="36zy1q1voruie">
      <h2 className={`text-lg font-bold mb-4 flex items-center ${dk.textHeading}`} data-id="element-18583" data-mp="372j4qu7q0ghq">
        <MapPinIcon className="w-5 h-5 mr-2 text-purple-500" data-id="element-18584" data-mp="3c1ncdgezy65y" />
        Location
      </h2>

      <div className="mb-6" data-id="element-18585" data-mp="3h0gs7fhra99a">
        <label className={`block text-sm font-medium mb-2 ${dk.textSecondary}`} data-id="element-18586" data-mp="3lxqi0w924mxn">
          Event Format
        </label>
        <div className="flex gap-4" data-id="element-18587" data-mp="hd3b9ue0xxrs">
          <label className="flex items-center" data-id="element-18588" data-mp="he4tqjxddmjj">
            <input type="radio" checked={!formData.isVirtual} onChange={() => updateField('isVirtual', false)} className="w-4 h-4 text-purple-600 border-gray-300 focus:ring-purple-500" data-id="element-18589" data-mp="cdqpze2swyrb" />
            <span className={`ml-2 text-sm ${dk.textPrimary}`} data-id="element-18590" data-mp="3elnjwo4cl9v">In-Person</span>
          </label>
          <label className="flex items-center" data-id="element-18591" data-mp="1029vjluguxfp">
            <input type="radio" checked={formData.isVirtual} onChange={() => updateField('isVirtual', true)} className="w-4 h-4 text-purple-600 border-gray-300 focus:ring-purple-500" data-id="element-18592" data-mp="v3gghozj6ydf" />
            <span className={`ml-2 text-sm ${dk.textPrimary}`} data-id="element-18593" data-mp="mbejk0oo807w">Virtual</span>
          </label>
        </div>
      </div>

      <div className="space-y-6" data-id="element-18594" data-mp="1ivdtvkimpygn">
        {showInPerson && <div className="space-y-4" data-id="element-18595" data-mp="1e19jsse2habc">
            <h3 className={`text-sm font-semibold uppercase tracking-wider ${dk.textMuted}`} data-id="element-18596" data-mp="38aogrhdi4f6a">
              In-Person Details
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-18597" data-mp="3d8zptpvz2lng">
              <div data-id="element-18598" data-mp="2yjtuid099fku">
                <label className={`block text-sm font-medium mb-1 ${dk.textSecondary}`} data-id="element-18599" data-mp="2ykbxdshfas1q">
                  Venue Name <span className="text-red-500" data-id="element-18600" data-mp="2pk6r5lnytzp5">*</span>
                </label>
                <input type="text" value={formData.location?.venueName || ''} onChange={e => updateNestedField('location', 'venueName', e.target.value)} className={`w-full p-2 rounded-md border ${dk.inputOnCard}`} placeholder="e.g. The Strand Bookstore" data-id="element-18601" data-mp="2pm9q4v73j360" />
              </div>
              <div data-id="element-18602" data-mp="itqrc4zqf1yu">
                <label className={`block text-sm font-medium mb-1 ${dk.textSecondary}`} data-id="element-18603" data-mp="iu8xbtc74ujy">
                  Venue Type
                </label>
                <select value={formData.location?.venueType || ''} onChange={e => updateNestedField('location', 'venueType', e.target.value)} className={`w-full p-2 rounded-md border ${dk.inputOnCard}`} data-id="element-18604" data-mp="1ycorxp1e273r">
                  <option value="" data-id="element-18605" data-mp="2m1q2bhts7sbo">Select Venue Type</option>
                  {VENUE_TYPES.map(type => <option key={type.value} value={type.value} data-id="element-18606" data-mp="2i1fkn7wmndgz">
                      {type.label}
                    </option>)}
                </select>
              </div>
            </div>

            <div data-id="element-18607" data-mp="2r2dui6tqyf7h">
              <label className={`block text-sm font-medium mb-1 ${dk.textSecondary}`} data-id="element-18608" data-mp="2r1vm6btt8aj7">
                Street Address
              </label>
              <input type="text" value={formData.location?.streetAddress || ''} onChange={e => updateNestedField('location', 'streetAddress', e.target.value)} className={`w-full p-2 rounded-md border ${dk.inputOnCard}`} placeholder="123 Main St" data-id="element-18609" data-mp="wo4j5v5zkowq" />
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-id="element-18610" data-mp="1d20yrmr5i36l">
              <div className="col-span-2 sm:col-span-1" data-id="element-18611" data-mp="fe0mcu8hzkkn">
                <label className={`block text-sm font-medium mb-1 ${dk.textSecondary}`} data-id="element-18612" data-mp="ffk30ly2fxmy">
                  City <span className="text-red-500" data-id="element-18613" data-mp="kaqy55d9ru09">*</span>
                </label>
                <input type="text" value={formData.location?.city || ''} onChange={e => updateNestedField('location', 'city', e.target.value)} className={`w-full p-2 rounded-md border ${dk.inputOnCard}`} data-id="element-18614" data-mp="pavij6hat8vr" />
              </div>
              <div className="col-span-2 sm:col-span-1" data-id="element-18615" data-mp="2ex5bhfz9u3i9">
                <label className={`block text-sm font-medium mb-1 ${dk.textSecondary}`} data-id="element-18616" data-mp="2euj4bhgalq0k">
                  State/Region
                </label>
                <input type="text" value={formData.location?.region || ''} onChange={e => updateNestedField('location', 'region', e.target.value)} className={`w-full p-2 rounded-md border ${dk.inputOnCard}`} data-id="element-18617" data-mp="25uci976j3hps" />
              </div>
              <div className="col-span-2 sm:col-span-1" data-id="element-18618" data-mp="1n564lykldgau">
                <label className={`block text-sm font-medium mb-1 ${dk.textSecondary}`} data-id="element-18619" data-mp="1n3la28ya1xcz">
                  Postcode
                </label>
                <input type="text" value={formData.location?.postcode || ''} onChange={e => updateNestedField('location', 'postcode', e.target.value)} className={`w-full p-2 rounded-md border ${dk.inputOnCard}`} data-id="element-18620" data-mp="1d708xypdp6ef" />
              </div>
              <div className="col-span-2 sm:col-span-1" data-id="element-18621" data-mp="pkmr14acu1f7">
                <label className={`block text-sm font-medium mb-1 ${dk.textSecondary}`} data-id="element-18622" data-mp="pm7q977m68je">
                  Country <span className="text-red-500" data-id="element-18623" data-mp="1hasncvbavk9c">*</span>
                </label>
                <select value={formData.location?.country || ''} onChange={e => updateNestedField('location', 'country', e.target.value)} className={`w-full p-2 rounded-md border ${dk.inputOnCard}`} data-id="element-18624" data-mp="1madys57npl2y">
                  <option value="" data-id="element-18625" data-mp="20aexpf4d2ifs">Select Country</option>
                  {COUNTRIES.map(country => <option key={country} value={country} data-id="element-18626" data-mp="251excs4v7elz">
                      {country}
                    </option>)}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-18627" data-mp="3ksqwpb65xofo">
              <div data-id="element-18628" data-mp="3kqd0gyzqy66j">
                <label className={`block text-sm font-medium mb-1 ${dk.textSecondary}`} data-id="element-18629" data-mp="3bfyxamou58c">
                  Room / Table Number
                </label>
                <input type="text" value={formData.location?.roomOrTable || ''} onChange={e => updateNestedField('location', 'roomOrTable', e.target.value)} className={`w-full p-2 rounded-md border ${dk.inputOnCard}`} placeholder="e.g. Hall A, Table 42" data-id="element-18630" data-mp="36sg525x145tg" />
              </div>
              <div data-id="element-18631" data-mp="vzvbzrm9edh1">
                <label className={`block text-sm font-medium mb-1 ${dk.textSecondary}`} data-id="element-18632" data-mp="vz24gofh7jxu">
                  Map Link
                </label>
                <input type="url" value={formData.location?.mapLink || ''} onChange={e => updateNestedField('location', 'mapLink', e.target.value)} className={`w-full p-2 rounded-md border ${dk.inputOnCard}`} placeholder="Google Maps URL" data-id="element-18633" data-mp="2kkj046eofxeg" />
              </div>
            </div>
          </div>}

        {showVirtual && <div className="space-y-4" data-id="element-18634" data-mp="389lft8pr3g6b">
            <h3 className={`text-sm font-semibold uppercase tracking-wider ${dk.textMuted}`} data-id="element-18635" data-mp="388k73aw7j5rr">
              Virtual Details
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-18636" data-mp="3i7j2vsvvj6xc">
              <div data-id="element-18637" data-mp="3i5osv9m54z81">
                <label className={`block text-sm font-medium mb-1 ${dk.textSecondary}`} data-id="element-18638" data-mp="1iung5rni256a">
                  Platform <span className="text-red-500" data-id="element-18639" data-mp="1nut9rddby1r0">*</span>
                </label>
                <select value={formData.virtualDetails?.platform || ''} onChange={e => updateNestedField('virtualDetails', 'platform', e.target.value)} className={`w-full p-2 rounded-md border ${dk.inputOnCard}`} data-id="element-18640" data-mp="1gar2z9rr8pwh">
                  <option value="" data-id="element-18641" data-mp="1z9d1cgvw02wp">Select Platform</option>
                  {VIRTUAL_PLATFORMS.map(platform => <option key={platform.value} value={platform.value} data-id="element-18642" data-mp="1ubbinx2di5lr">
                      {platform.label}
                    </option>)}
                </select>
              </div>
              <div data-id="element-18643" data-mp="1kbay67srpjfz">
                <label className={`block text-sm font-medium mb-1 ${dk.textSecondary}`} data-id="element-18644" data-mp="1kdx46300fqw3">
                  Access Link
                </label>
                <input type="url" value={formData.virtualDetails?.accessLink || ''} onChange={e => updateNestedField('virtualDetails', 'accessLink', e.target.value)} className={`w-full p-2 rounded-md border ${dk.inputOnCard}`} placeholder="https://..." data-id="element-18645" data-mp="8v47jyqiv3x8" />
              </div>
            </div>

            <div className="flex items-center" data-id="element-18646" data-mp="3usrsfozitz4r">
              <input type="checkbox" id="replayAvailable" checked={formData.virtualDetails?.replayAvailable || false} onChange={e => updateNestedField('virtualDetails', 'replayAvailable', e.target.checked)} className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500" data-id="element-18647" data-mp="3qoaazwgmvxx5" />
              <label htmlFor="replayAvailable" className={`ml-2 text-sm font-medium ${dk.textPrimary}`} data-id="element-18648" data-mp="kuzoyquqpvlb">
                Replay will be available after the event
              </label>
            </div>
          </div>}
      </div>
    </div>;
};